import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../api/client.js';
import type { Invoice, InvoicePage, Supplier } from '../../api/types.js';
import { formatAmount, formatInvoiceDate } from '../invoices/format.js';

interface Props {
  tenantId: string;
  supplier: Supplier;
  onClose: () => void;
}

function useLastInvoicesBySupplier(tenantId: string, siren: string | null) {
  return useQuery<Invoice[]>({
    queryKey: ['supplier-last-invoices', tenantId, siren ?? '_anon'],
    queryFn: async () => {
      if (!siren) return [];
      const page = await apiFetch<InvoicePage>(
        `/invoices?tenantId=${encodeURIComponent(tenantId)}&direction=INBOUND&limit=3`,
      );
      return page.items.filter((i) => i.sender?.siren === siren).slice(0, 3);
    },
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
}

export function SupplierDrawer({ tenantId, supplier, onClose }: Props) {
  const last = useLastInvoicesBySupplier(tenantId, supplier.siren ?? null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="modal__backdrop"
      role="presentation"
      data-testid="supplier-drawer"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        className={`drawer${mounted ? ' drawer--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="supplier-drawer-title"
      >
        <header className="modal__head">
          <div>
            <div className="eyebrow">Fournisseur</div>
            <h2 id="supplier-drawer-title" className="display modal__title">
              {supplier.name ?? '—'}
            </h2>
          </div>
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label="Fermer"
            data-testid="supplier-drawer-close"
          >
            ×
          </button>
        </header>
        <div className="drawer__body">
          <dl className="invoice-detail__facts">
            <div>
              <dt>SIREN</dt>
              <dd>{supplier.siren ?? '—'}</dd>
            </div>
            <div>
              <dt>Factures reçues</dt>
              <dd>{supplier.invoiceCount}</dd>
            </div>
            <div>
              <dt>Total facturé</dt>
              <dd>{formatAmount(supplier.totalAmount, supplier.currency ?? 'EUR')}</dd>
            </div>
            <div>
              <dt>Dernière facture</dt>
              <dd>{supplier.lastInvoiceAt ? formatInvoiceDate(supplier.lastInvoiceAt) : '—'}</dd>
            </div>
          </dl>

          <div className="eyebrow" style={{ marginTop: 16 }}>
            3 dernières factures
          </div>
          {last.isLoading ? (
            <p className="muted">Chargement…</p>
          ) : last.data && last.data.length > 0 ? (
            <ul className="drawer__invoices" data-testid="supplier-drawer-invoices">
              {last.data.map((inv) => (
                <li key={inv.primaryKey} className="drawer__invoice-row">
                  <Link
                    to={`/t/${tenantId}/invoices/${encodeURIComponent(inv.primaryKey)}`}
                    onClick={onClose}
                  >
                    {inv.invoiceNumber ?? inv.primaryKey.split('__').slice(-1)[0]}
                  </Link>
                  <span className="muted">
                    {inv.invoiceDate ? formatInvoiceDate(inv.invoiceDate) : '—'}
                  </span>
                  <span>{formatAmount(inv.amountTotal ?? null, inv.currency ?? 'EUR')}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Aucune facture trouvée.</p>
          )}
        </div>
      </aside>
    </div>
  );
}
